import { message } from "antd";
import dayjs from "dayjs";
import type { ReactNode } from "react";

import type { UserDetailVO } from "@/api/accounts/type";
import type { SessionUser } from "@/types/auth";

/** localStorage 中 JWT 访问令牌的键名 */
const TOKEN_KEY = "token";

/** 详情页、表格空值统一占位符 */
const EMPTY_PLACEHOLDER = "-";

/** 默认日期时间格式（列表、详情通用） */
const DEFAULT_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

/** 复制图片成功时的默认提示文案 */
const DEFAULT_COPY_IMAGE_MSG = "图片已复制到剪贴板，可粘贴到其他地方！";

/**
 * 读取本地存储的访问令牌
 * @returns token 字符串；未登录时为 null
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 写入访问令牌（登录成功后调用）
 * @param token 后端 LoginResultVO.token
 */
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * 清除访问令牌（退出登录、bootstrap 失败、401 时调用）
 */
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * 将 getCurrentUser 返回的全量详情映射为全局 SessionUser
 * 仅保留布局、权限展示所需字段，避免 loginLogs 等大对象进入 jotai
 * @param detail 后端 UserDetailVO
 * @returns 写入 currentUserAtom 的会话用户
 */
export const mapCurrentUser = (detail: UserDetailVO): SessionUser => ({
  id: detail.id,
  userCode: detail.userCode,
  username: detail.userCode,
  name: detail.name,
  phone: detail.phone,
  defaultRole: detail.defaultRole,
  opsUserRoleList: detail.opsUserRoleList,
  nickname: detail.name || detail.profile?.nickname,
  avatar: detail.profile?.avatarUrl,
});

/**
 * 空值占位：null / undefined / 空字符串时展示「-」
 * 用于详情行、表格 render，避免页面出现空白
 * @param value 原始展示值，支持 ReactNode
 * @returns 有值则原样返回，否则返回「-」
 */
export const emptyPlaceholder = (value?: string | number | null | ReactNode) =>
  value ?? EMPTY_PLACEHOLDER;

/**
 * 格式化日期时间字符串
 * @param date ISO 或后端返回的时间字符串
 * @param format dayjs 格式，默认 YYYY-MM-DD HH:mm:ss
 * @returns 格式化结果；无有效日期时返回「-」
 */
export const formatDate = (
  date: string | undefined | null,
  format: string = DEFAULT_DATETIME_FORMAT,
): string => (date ? dayjs(date).format(format) : EMPTY_PLACEHOLDER);

/**
 * 触发浏览器下载 Blob 文件
 * 通过临时 <a> 标签 + object URL 实现，调用后会 revoke 由浏览器回收
 * @param blob 文件二进制
 * @param filename 保存文件名，空则由浏览器决定
 */
export const downloadFile = (blob: Blob, filename?: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.setAttribute("href", objectUrl);
  anchor.setAttribute("download", filename || "");
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

/**
 * 将图片 Blob 写入系统剪贴板
 * 需 HTTPS 或 localhost 且用户授权 clipboard 权限
 * @param image PNG 等图片 Blob
 * @param msg 成功提示，不传则用默认文案
 */
export const copyImage = (image: Blob, msg?: string) => {
  void navigator.clipboard.write([new ClipboardItem({ "image/png": image })]);
  message.success(msg || DEFAULT_COPY_IMAGE_MSG);
};

export { hasActiveListFilters } from "./listQuery";
